import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  signal,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

// PrimeNG Imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputOtpModule } from 'primeng/inputotp';
import { CardModule } from 'primeng/card';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

// Core Services
import { AuthUserService } from 'src/app/core/services/auth-user.service';
import { ToastrShowService } from 'src/app/core/services/toastr.service';
import { LoggerService } from 'src/app/core/services/logger.service';

// Constants
import {
  BROKER_ADMIN_BSE_PASSWORD,
  FORGET_PASSWORD
} from 'src/app/core/constants/login.constants';
import { IMessage } from 'src/app/core/models/product-admin';

// Types
interface BSEPasswordStep {
  step: 'email' | 'otp' | 'credentials' | 'confirmation';
  title: string;
  description: string;
}

@Component({
  selector: 'app-broker-bse-password-update',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputOtpModule,
    CardModule
  ],
  templateUrl: './broker-bse-password-update.component.html',
  styleUrls: ['./broker-bse-password-update.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BrokerBsePasswordUpdateComponent implements OnDestroy {
  private readonly logger = inject(LoggerService);
  private readonly authService = inject(AuthUserService);
  private readonly toastrService = inject(ToastrShowService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  // Signals
  currentStepIndex = signal(0);
  isLoading = signal(false);
  userEmail = signal('');
  securityCode = signal('');
  canResend = signal(false);
  resendTimer = signal(60);
  // tracks whether user has attempted to submit the email (clicked Generate OTP)
  submitEmailAttempt = signal(false);
  emailError = signal('');
  otpError = signal('');
  credentialsError = signal('');
  brokerAdminUserId = signal('');

  private resendInterval?: number;

  // Forms
  emailForm = this.formBuilder.group({
    email: [
      '',
      [
        Validators.required,
        Validators.email,
        Validators.minLength(5),
        Validators.pattern(/^\S+@\S+\.\S+$/)
      ]
    ]
  });

  otpForm = this.formBuilder.group({
    otp: ['', [Validators.required, Validators.minLength(6)]]
  });

  credentialsForm = this.formBuilder.group({
    bseUserId: ['', Validators.required],
    bseMemberId: ['', Validators.required],
    newBsePassword: ['', Validators.required]
  });

  // Steps configuration
  private readonly steps: BSEPasswordStep[] = [
    {
      step: 'email',
      title: 'BSE Star MF Credentials',
      description:
        'Enter your email ID to receive a verification code for adding your credentials to GemFin'
    },
    {
      step: 'otp',
      title: 'OTP Verification',
      description:
        'Enter the one time password received on the email ID provided.'
    },
    {
      step: 'credentials',
      title: 'BSE Star Credentials',
      description:
        'Provide your BSE credentials, in order to initiate transactions on behalf of your clients, which will be stored securely on our platform.'
    },
    {
      step: 'confirmation',
      title: 'BSE Password Saved',
      description:
        'BSE Password Saved! Your new password is now secure with us. Please remember to keep it safe and confidential.'
    }
  ];

  // Computed signals
  currentStep = computed(() => this.steps[this.currentStepIndex()]);

  isEmailFormValid(): boolean | undefined {
    const emailControl = this.emailForm.get('email');
    return emailControl?.valid && emailControl?.touched;
  }

  isOtpFormValid(): boolean | string | null | undefined {
    const otpValue = this.otpForm.get('otp')?.value;
    const otpControl = this.otpForm.get('otp');
    return otpValue && otpValue.length === 6 && otpControl?.touched;
  }

  isCredentialsFormValid(): boolean {
    return this.credentialsForm.valid && this.credentialsForm.touched;
  }

  constructor() {
    // Get broker admin user ID from config data
    const modalData = this.config.data?.modalData;
    if (modalData?.brokerAdminUserId) {
      this.brokerAdminUserId.set(modalData.brokerAdminUserId);
    }

    this.startResendTimer();

    // Clear server-side email errors when user edits the email and it becomes valid
    const emailControl = this.emailForm.get('email');
    emailControl?.valueChanges.subscribe(() => {
      if (emailControl.valid) {
        this.emailError.set('');
        // clear submit attempt once input corrected
        this.submitEmailAttempt.set(false);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }
  }

  /**
   * Generate OTP for email verification
   */
  generateOTP(): void {
    const emailControl = this.emailForm.get('email');

    // Mark as touched to show validation errors
    emailControl?.markAsTouched();
    // mark that user attempted to submit so errors show even if not focused
    this.submitEmailAttempt.set(true);

    const email = emailControl?.value;

    if (!email || this.emailForm.invalid) {
      // Let template show client-side validation errors (touched / submitEmailAttempt)
      return;
    }

    this.isLoading.set(true);
    this.emailError.set('');
    this.userEmail.set(email);

    this.authService.forgotPassword(email).subscribe({
      next: (response: IMessage) => {
        this.isLoading.set(false);
        if (response) {
          this.authService.setForgetmail(email);
          this.toastrService.showSuccess(FORGET_PASSWORD.OTP_SENT);
          // clear submit attempt and move to next step
          this.submitEmailAttempt.set(false);
          this.nextStep();
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        // show server-provided error message (e.g., email not registered)
        this.emailError.set(
          error?.error?.message ?? FORGET_PASSWORD.EMAIL_NOT_REG
        );
        this.logger.error(
          'Failed to generate OTP for BSE setup',
          'BrokerBsePasswordUpdateComponent.generateOTP',
          error
        );
      }
    });
  }

  /**
   * Verify OTP
   */
  verifyOTP(): void {
    const otpControl = this.otpForm.get('otp');

    // Mark as touched to show validation errors
    otpControl?.markAsTouched();

    const otpValue = otpControl?.value;

    if (!otpValue || otpValue.length !== 6) {
      this.otpError.set('Please enter a valid 6-digit OTP');
      this.changeDetectorRef.markForCheck();
      return;
    }

    this.isLoading.set(true);
    this.otpError.set('');
    this.securityCode.set(otpValue);

    this.authService.validateOtp(this.userEmail(), otpValue).subscribe({
      next: (response) => {
        this.isLoading.set(false);
        if (response.message === BROKER_ADMIN_BSE_PASSWORD.OTP_VALIDATE) {
          this.toastrService.showSuccess(
            BROKER_ADMIN_BSE_PASSWORD.OTP_VALIDATE
          );
          this.nextStep();
        } else if (response.message === BROKER_ADMIN_BSE_PASSWORD.OTP_EXPIRE) {
          this.otpError.set(BROKER_ADMIN_BSE_PASSWORD.OTP_EXPIRE);
        }
      },
      error: (error) => {
        this.isLoading.set(false);
        this.otpError.set(
          error?.error?.message ?? BROKER_ADMIN_BSE_PASSWORD.ERR
        );
        this.logger.error(
          'OTP verification failed for BSE setup',
          'BrokerBsePasswordUpdateComponent.verifyOTP',
          error
        );
      }
    });
  }

  /**
   * Submit BSE credentials
   */
  submitBSECredentials(): void {
    // Mark all fields as touched to show validation errors
    Object.keys(this.credentialsForm.controls).forEach((key) => {
      this.credentialsForm.get(key)?.markAsTouched();
    });

    if (this.credentialsForm.invalid) {
      this.credentialsError.set('Please fill all required fields');
      this.changeDetectorRef.markForCheck();
      return;
    }

    const formValue = this.credentialsForm.value;
    this.isLoading.set(true);
    this.credentialsError.set('');

    this.authService
      .bsePasswordSetUp(
        this.brokerAdminUserId(),
        this.securityCode(),
        '', // ARN (empty for now)
        formValue.newBsePassword!,
        formValue.bseMemberId!,
        formValue.bseUserId!
      )
      .subscribe({
        next: (response: IMessage) => {
          this.isLoading.set(false);
          if (response) {
            this.nextStep();
            this.toastrService.showSuccess(FORGET_PASSWORD.PASS_UPDATE);
          }
        },
        error: (error) => {
          this.isLoading.set(false);
          this.credentialsError.set(
            error?.error?.message ?? FORGET_PASSWORD.SAME_PASSWORD
          );
          this.logger.error(
            'Failed to save BSE credentials',
            'BrokerBsePasswordUpdateComponent.submitBSECredentials',
            error
          );
        }
      });
  }

  /**
   * Resend OTP
   */
  resendOTP(): void {
    this.canResend.set(false);
    this.resendTimer.set(60);
    this.startResendTimer();

    // Clear OTP form
    this.otpForm.reset();
    this.otpError.set('');
    this.changeDetectorRef.markForCheck();

    // Regenerate OTP
    this.generateOTP();

    this.logger.userAction(
      'OTP resend requested for BSE setup',
      { email: this.userEmail() },
      'BrokerBsePasswordUpdateComponent.resendOTP'
    );
  }

  /**
   * Start resend timer
   */
  private startResendTimer(): void {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
    }

    this.resendInterval = window.setInterval(() => {
      const current = this.resendTimer();
      if (current <= 1) {
        this.canResend.set(true);
        if (this.resendInterval) {
          clearInterval(this.resendInterval);
        }
      } else {
        this.resendTimer.set(current - 1);
      }
    }, 1000);
  }

  /**
   * Move to next step
   */
  nextStep(): void {
    if (this.currentStepIndex() < this.steps.length - 1) {
      this.currentStepIndex.set(this.currentStepIndex() + 1);

      // Auto-verify OTP when complete (for step 2)
      if (this.currentStep().step === 'otp') {
        // entering OTP step, ensure submitEmailAttempt is reset so email errors don't persist
        this.submitEmailAttempt.set(false);
        this.otpForm
          .get('otp')
          ?.valueChanges.subscribe((value: string | null) => {
            if (value && value.length === 6) {
              setTimeout(() => this.verifyOTP(), 500);
            }
          });
      }
    }
  }

  /**
   * Close modal
   */
  closeModal(): void {
    this.dialogRef.close(false);
  }

  /**
   * Complete BSE setup and logout
   */
  completeBSESetup(): void {
    this.dialogRef.close(true);
    this.toastrService.showInfo('You are logging out');
    this.logout();
  }

  /**
   * Logout user
   */
  private logout(): void {
    localStorage.clear();
    this.authService.isLoggedIn.set(false);
    this.router.navigate(['/login']);
  }
}
